import type {
    CreateKnowledgeOperationCommand,
    KnowledgeOperation,
    KnowledgeOperationCreated,
    KnowledgeOperationList,
    KnowledgeOperationListStatus,
    KnowledgeWorkItemList,
} from '@/domain/knowledgeOperations';
import {
    clearPendingRequestKey,
    pendingRequestKey,
    shouldRetainRequestKey,
} from '@/lib/requestIdempotency';
import {
    buildApiUrl,
    getAdminApiBaseUrl,
    getSupabaseAccessToken,
    parseErrorDetail,
    type ServiceErrorFactory,
} from './adminApi';

const OPERATIONS_PATH = '/admin/knowledge/operations';

export class KnowledgeOperationsError extends Error {
    status: number | null;

    constructor(message: string, status: number | null = null) {
        super(message);
        this.name = 'KnowledgeOperationsError';
        this.status = status;
    }
}

const fail: ServiceErrorFactory = (message, status = null) =>
    new KnowledgeOperationsError(message, status);

function buildAdminUrl(path: string, query?: URLSearchParams): URL {
    const url = buildApiUrl(getAdminApiBaseUrl(fail), path);
    if (query) url.search = query.toString();
    return url;
}

async function adminFetch<T>(path: string, init: RequestInit, query?: URLSearchParams): Promise<T> {
    const accessToken = await getSupabaseAccessToken(fail);
    let response: Response;
    try {
        response = await fetch(buildAdminUrl(path, query), {
            ...init,
            headers: {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
                ...init.headers,
            },
        });
    } catch (error) {
        const message = error instanceof Error ? error.message : 'Unknown network error';
        throw new KnowledgeOperationsError(`Network error: ${message}`);
    }

    if (!response.ok) {
        const detail = await parseErrorDetail(response);
        throw new KnowledgeOperationsError(
            detail ?? `Knowledge operation request failed (${response.status})`,
            response.status
        );
    }

    try {
        return (await response.json()) as T;
    } catch {
        throw new KnowledgeOperationsError('Failed to parse knowledge operation response');
    }
}

export async function createKnowledgeOperation(
    command: CreateKnowledgeOperationCommand
): Promise<KnowledgeOperationCreated> {
    if (command.type !== 'website_selected_refresh') {
        return adminFetch<KnowledgeOperationCreated>(OPERATIONS_PATH, {
            method: 'POST',
            headers: { 'Idempotency-Key': command.idempotency_key },
            body: JSON.stringify(command),
        });
    }

    const expectedSnapshots = Object.entries(command.expected_snapshot_ids ?? {}).sort(
        ([left], [right]) => left.localeCompare(right)
    );
    const scope = JSON.stringify([
        'website-selected-refresh',
        command.workspace_id,
        [...command.document_ids].sort(),
        expectedSnapshots,
        command.force_fetch ?? false,
    ]);
    const requestKey = pendingRequestKey(scope);
    const idempotencyKey = `selected-refresh:${requestKey}`;
    const stableCommand = { ...command, idempotency_key: idempotencyKey };
    try {
        const result = await adminFetch<KnowledgeOperationCreated>(OPERATIONS_PATH, {
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            body: JSON.stringify(stableCommand),
        });
        clearPendingRequestKey(scope);
        return result;
    } catch (error) {
        if (!shouldRetainRequestKey(error)) clearPendingRequestKey(scope);
        throw error;
    }
}

/**
 * Promote a lite tenant to the advanced knowledge tier.
 *
 * The tier is never written from the browser: this queues a durable rebuild of
 * the tenant's entity graph, and the control plane activates advanced
 * retrieval itself once that rebuild succeeds. The request key is cleared on
 * success so a later attempt — after a failed rebuild — creates a new
 * operation instead of resolving to the failed one forever.
 */
export async function startKnowledgeModeMigration(
    workspaceId: string
): Promise<KnowledgeOperationCreated> {
    const scope = `knowledge-mode-migration:${workspaceId.toLowerCase()}`;
    const requestKey = pendingRequestKey(scope);
    try {
        const result = await adminFetch<KnowledgeOperationCreated>(
            `${OPERATIONS_PATH}/knowledge-mode-migrations`,
            {
                method: 'POST',
                headers: { 'Idempotency-Key': requestKey },
                body: JSON.stringify({ workspace_id: workspaceId }),
            }
        );
        clearPendingRequestKey(scope);
        return result;
    } catch (error) {
        if (!shouldRetainRequestKey(error)) clearPendingRequestKey(scope);
        throw error;
    }
}

export function listKnowledgeOperations(
    workspaceId: string,
    status: KnowledgeOperationListStatus = 'active',
    cursor?: string | null
): Promise<KnowledgeOperationList> {
    const query = new URLSearchParams({ workspace_id: workspaceId, status });
    if (cursor) query.set('cursor', cursor);
    return adminFetch<KnowledgeOperationList>(OPERATIONS_PATH, { method: 'GET' }, query);
}

export function getKnowledgeOperation(
    operationId: string,
    workspaceId: string
): Promise<KnowledgeOperation> {
    return adminFetch<KnowledgeOperation>(
        `${OPERATIONS_PATH}/${encodeURIComponent(operationId)}?workspace_id=${encodeURIComponent(workspaceId)}`,
        {
            method: 'GET',
        }
    );
}

export function listKnowledgeOperationItems(
    operationId: string,
    options: { workspaceId: string; status?: string; cursor?: string | null }
): Promise<KnowledgeWorkItemList> {
    const query = new URLSearchParams({ workspace_id: options.workspaceId });
    if (options.status) query.set('status', options.status);
    if (options.cursor) query.set('cursor', options.cursor);
    return adminFetch<KnowledgeWorkItemList>(
        `${OPERATIONS_PATH}/${encodeURIComponent(operationId)}/items`,
        { method: 'GET' },
        query
    );
}

export async function retryKnowledgeOperation(
    operationId: string,
    workspaceId: string
): Promise<KnowledgeOperationCreated> {
    const scope = `knowledge-operation-retry:${workspaceId}:${operationId}`;
    const requestKey = pendingRequestKey(scope);
    try {
        const result = await adminFetch<KnowledgeOperationCreated>(
            `${OPERATIONS_PATH}/${encodeURIComponent(operationId)}:retry?workspace_id=${encodeURIComponent(workspaceId)}`,
            {
                method: 'POST',
                headers: { 'Idempotency-Key': requestKey },
            }
        );
        clearPendingRequestKey(scope);
        return result;
    } catch (error) {
        if (!shouldRetainRequestKey(error)) clearPendingRequestKey(scope);
        throw error;
    }
}

export function cancelKnowledgeOperation(
    operationId: string,
    workspaceId: string
): Promise<KnowledgeOperation> {
    return adminFetch<KnowledgeOperation>(
        `${OPERATIONS_PATH}/${encodeURIComponent(operationId)}:cancel?workspace_id=${encodeURIComponent(workspaceId)}`,
        { method: 'POST' }
    );
}
